import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer'

type FormFields = {
  name: string
  email: string
  phone: string
  service: string
  message: string
  company: string // honeypot
}

type FormErrors = Partial<Record<keyof FormFields, string>>

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_REGEX = /^[+]?[\d\s\-().]{7,20}$/

const initialValues: FormFields = {
  name: '',
  email: '',
  phone: '',
  service: '',
  message: '',
  company: '',
}

const Contact = () => {
  const formRef = useRef<HTMLFormElement>(null)
  const statusRef = useRef<HTMLDivElement>(null)

  const [values, setValues] = useState<FormFields>(initialValues)
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Partial<Record<keyof FormFields, boolean>>>({})

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [status, setStatus] = useState<{
    type: 'success' | 'error' | null
    message: string
    hide?: boolean
  }>({ type: null, message: '' })

  useEffect(() => {
    if (status.type !== 'success') return

    // Start fade-out at 55s, remove at 60s
    const fadeTimer = setTimeout(() => {
      setStatus((prev) => ({ ...prev, hide: true }))
    }, 55_000)

    const removeTimer = setTimeout(() => {
      setStatus({ type: null, message: '' })
    }, 60_000)

    return () => {
      clearTimeout(fadeTimer)
      clearTimeout(removeTimer)
    }
  }, [status.type, status.message]) // ⬅️ don't depend on `hide`, or it re-triggers

  // ---------- Validation ----------
  const validateField = (field: keyof FormFields, value: string): string => {
    const trimmed = value.trim()

    switch (field) {
      case 'name':
        if (!trimmed) return 'Name is required.'
        if (trimmed.length < 2) return 'Name must be at least 2 characters.'
        if (trimmed.length > 100) return 'Name must be under 100 characters.'
        return ''

      case 'email':
        if (!trimmed) return 'Email is required.'
        if (!EMAIL_REGEX.test(trimmed)) return 'Please enter a valid email address.'
        if (trimmed.length > 150) return 'Email is too long.'
        return ''

      case 'phone':
        if (!trimmed) return ''
        if (!PHONE_REGEX.test(trimmed))
          return 'Please enter a valid phone number (7-20 digits).'
        return ''

      case 'service':
        if (!trimmed) return 'Please select a service.'
        return ''

      case 'message':
        if (!trimmed) return 'Message is required.'
        if (trimmed.length < 10) return 'Message must be at least 10 characters.'
        if (trimmed.length > 5000) return 'Message must be under 5000 characters.'
        return ''

      case 'company':
        return ''

      default:
        return ''
    }
  }

  const validateAll = (fields: FormFields): FormErrors => {
    const newErrors: FormErrors = {}
    ;(Object.keys(fields) as (keyof FormFields)[]).forEach((key) => {
      const error = validateField(key, fields[key])
      if (error) newErrors[key] = error
    })
    return newErrors
  }

  // ---------- Handlers ----------
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    const field = name as keyof FormFields

    setValues((prev) => ({ ...prev, [field]: value }))

    if (touched[field]) {
      setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }))
    }
  }

  const handleBlur = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    const field = name as keyof FormFields

    setTouched((prev) => ({ ...prev, [field]: true }))
    setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }))
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const allTouched: Partial<Record<keyof FormFields, boolean>> = {}
    ;(Object.keys(values) as (keyof FormFields)[]).forEach((k) => (allTouched[k] = true))
    setTouched(allTouched)

    const validationErrors = validateAll(values)
    setErrors(validationErrors)

    if (Object.keys(validationErrors).length > 0) {
      setStatus({
        type: 'error',
        message: 'Please fix the highlighted fields before submitting.',
      })
      const firstError = Object.keys(validationErrors)[0]
      const el = formRef.current?.querySelector<HTMLElement>(`[name="${firstError}"]`)
      el?.focus()
      return
    }

    setIsSubmitting(true)
    setStatus({ type: null, message: '' })

    try {
      const apiUrl = process.env.REACT_APP_MAIL_API_URL || 'http://localhost:5000'
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to send message')
      }

      setStatus({
        type: 'success',
        message:
          'Thank you! Your message has been sent. We will get back to you within 24 hours.',
      })

      setValues(initialValues)
      setErrors({})
      setTouched({})
      formRef.current?.reset()

      requestAnimationFrame(() => {
        statusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    } catch (error) {
      console.error('Contact form error:', error)
      setStatus({
        type: 'error',
        message:
          error instanceof Error
            ? error.message
            : 'Oops! Something went wrong. Please try again or email us directly at hello@themarketingfeeds.com.',
      })

      requestAnimationFrame(() => {
        statusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // ---------- Helpers ----------
  const showError = (field: keyof FormFields) =>
    touched[field] && errors[field] ? errors[field] : ''

  const inputClass = (field: keyof FormFields) =>
    `mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary ${
      showError(field) ? 'border-red-500 bg-red-50' : 'border-gray-300'
    } ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* ============ FULL-PAGE LOADER OVERLAY ============ */}
      {isSubmitting && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Sending your message"
        >
          <div className="bg-white rounded-lg shadow-2xl p-8 flex flex-col items-center gap-4 max-w-sm mx-4">
            {/* Spinner */}
            <svg
              className="animate-spin h-12 w-12 text-primary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>

            <h3 className="text-lg font-semibold text-gray-900">
              Sending your message...
            </h3>
            <p className="text-sm text-gray-500 text-center">
              Please wait a moment while we deliver your message.
            </p>
          </div>
        </div>
      )}

      <Header />
      <main className="flex-grow">
        <div className="relative bg-gray-900">
          <div className="absolute inset-0">
            <img
              className="w-full h-full object-cover opacity-30"
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2850&q=80"
              alt="Digital marketing analytics"
            />
          </div>
          <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
                Contact Us
              </h1>
              <p className="mt-4 max-w-2xl text-xl text-gray-100 mx-auto">
                Have questions about our services? Get in touch with our team today.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Send us a message
              </h2>

              {status.type && (
                <div
                  ref={statusRef}
                  className={`mb-6 rounded-md p-4 text-sm transition-opacity duration-500 ease-in-out ${
                    status.hide ? 'opacity-0' : 'opacity-100'
                  } ${
                    status.type === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                  role="alert"
                  aria-live="polite"
                >
                  {status.message}
                </div>
              )}

              <form
                ref={formRef}
                onSubmit={handleSubmit}
                noValidate
                className="space-y-6"
              >
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={100}
                    autoComplete="name"
                    disabled={isSubmitting}
                    aria-invalid={!!showError('name')}
                    aria-describedby={showError('name') ? 'name-error' : undefined}
                    className={inputClass('name')}
                  />
                  {showError('name') && (
                    <p id="name-error" className="mt-1 text-sm text-red-600">
                      {showError('name')}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={150}
                    autoComplete="email"
                    disabled={isSubmitting}
                    aria-invalid={!!showError('email')}
                    aria-describedby={showError('email') ? 'email-error' : undefined}
                    className={inputClass('email')}
                  />
                  {showError('email') && (
                    <p id="email-error" className="mt-1 text-sm text-red-600">
                      {showError('email')}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={values.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={20}
                    autoComplete="tel"
                    disabled={isSubmitting}
                    aria-invalid={!!showError('phone')}
                    aria-describedby={showError('phone') ? 'phone-error' : undefined}
                    className={inputClass('phone')}
                  />
                  {showError('phone') && (
                    <p id="phone-error" className="mt-1 text-sm text-red-600">
                      {showError('phone')}
                    </p>
                  )}
                </div>

                {/* Service */}
                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700">
                    Service of Interest <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={values.service}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    aria-invalid={!!showError('service')}
                    aria-describedby={showError('service') ? 'service-error' : undefined}
                    className={inputClass('service')}
                  >
                    <option value="">Select a service</option>
                    <option value="SaaS Development">SaaS Development</option>
                    <option value="Email Campaigns">Email Campaigns</option>
                    <option value="SEO Strategy">SEO Strategy</option>
                    <option value="Google & Facebook Ads">Google &amp; Facebook Ads</option>
                    <option value="Social Media Campaigns">Social Media Campaigns</option>
                    <option value="Content Marketing">Content Marketing</option>
                    <option value="Other">Other</option>
                  </select>
                  {showError('service') && (
                    <p id="service-error" className="mt-1 text-sm text-red-600">
                      {showError('service')}
                    </p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={values.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={5000}
                    disabled={isSubmitting}
                    aria-invalid={!!showError('message')}
                    aria-describedby={showError('message') ? 'message-error' : undefined}
                    className={inputClass('message')}
                  ></textarea>
                  <div className="flex justify-between mt-1">
                    {showError('message') ? (
                      <p id="message-error" className="text-sm text-red-600">
                        {showError('message')}
                      </p>
                    ) : (
                      <span />
                    )}
                    <span className="text-xs text-gray-400">
                      {values.message.length}/5000
                    </span>
                  </div>
                </div>

                {/* Honeypot */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="company">Company</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={values.company}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {/* Submit button */}
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors duration-200 ${
                      isSubmitting
                        ? 'bg-primary-400 cursor-not-allowed'
                        : 'bg-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      'Submit'
                    )}
                  </button>
                </div>
              </form>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Contact Information
              </h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div className="ml-3 text-base text-gray-500">
                    <p>+234 9126 818 976</p>
                    <p className="mt-1">Mon-Fri 9am-6pm GMT</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="ml-3 text-base text-gray-500">
                    <p>contact@themarketingfeeds.com</p>
                    <p className="mt-1">We typically reply within 24 hours</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Follow us</h3>
                <div className="flex space-x-6">
                  <a href="#" className="text-gray-400 hover:text-gray-500">
                    <span className="sr-only">Facebook</span>
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                    </svg>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-gray-500">
                    <span className="sr-only">Instagram</span>
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                    </svg>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-gray-500">
                    <span className="sr-only">Twitter</span>
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-gray-500">
                    <span className="sr-only">LinkedIn</span>
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Contact